/**
 * Bulk Event Import — wizard for instructors to upload many events at
 * once from an .xlsx or .csv spreadsheet. Four steps:
 *   1. Upload   — download template + dropzone for the spreadsheet
 *   2. Preview  — per-row validation table; user reviews before commit
 *   3. Confirm  — summary + optional combined PayPal checkout
 *   4. Success  — link out to My Events to review drafts
 *
 * Hard scope (locked in with user):
 *   - drafts only (no instant publish)
 *   - one row = one event (no recurrence in v1)
 *   - banked `event_free_posts_remaining` auto-applied to cheapest rows
 *     by the backend
 *   - combined PayPal optional; instructor can skip and pay each draft
 *     individually from My Events with the existing flow
 */
import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
  FileSpreadsheet, Upload, Download, CheckCircle2, AlertCircle,
  Loader2, Sparkles, ArrowLeft, ArrowRight, FileCheck2,
  BookOpen, ChevronDown, ChevronUp,
} from 'lucide-react';

const ACCEPT_TYPES = '.xlsx,.csv';
const STEPS = ['Upload', 'Preview', 'Confirm', 'Done'];

function StepBar({ step }) {
  return (
    <div className="flex items-center justify-between mb-8 text-xs">
      {STEPS.map((label, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={label} className="flex-1 flex items-center">
            <div
              data-testid={`bulk-import-step-${idx}`}
              className={`flex items-center gap-2 ${active ? 'text-[#b456df] font-semibold' : done ? 'text-[#1f8a6a]' : 'text-slate-400'}`}
            >
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full border ${active ? 'border-[#b456df] bg-[#b456df]/10' : done ? 'border-[#1f8a6a] bg-[#1f8a6a]/10' : 'border-slate-300 bg-slate-50'}`}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : idx}
              </span>
              {label}
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-px mx-2 ${done ? 'bg-[#1f8a6a]/40' : 'bg-slate-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function BulkImportEventsPage() {
  const { user } = useAuth();
  const isInstructor = !!user?.is_instructor || user?.role === 'admin';
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { rows, valid_count, ... }
  const [committing, setCommitting] = useState(false);
  const [batch, setBatch] = useState(null); // { batch_id, draft_event_ids, total_due_usd, free_posts_applied, row_count }
  const [paypalOrder, setPaypalOrder] = useState(null); // { order_id, amount_due }
  const [paypalReady, setPaypalReady] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState('');
  const [showRules, setShowRules] = useState(false);
  const fileInputRef = useRef(null);

  // Gate non-instructors before render — admins allowed for testing.
  if (!isInstructor) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <Sparkles className="w-12 h-12 text-[#b456df] mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">Bulk Upload is for Instructors</h1>
        <p className="text-slate-600 mb-6">
          This power tool lets you upload many events at once from a spreadsheet.
          To unlock it, mark yourself as an instructor on your profile.
        </p>
        <Link to="/profile">
          <Button className="rounded-full bg-[#b456df] hover:bg-[#9a3ec4]">Go to Profile</Button>
        </Link>
      </div>
    );
  }

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/events/bulk-import/template.xlsx', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'meet-for-mahjong-events-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download template — please try again.');
    }
  };

  const handleFile = (f) => {
    if (!f) return;
    const ok = /\.(xlsx|csv)$/i.test(f.name);
    if (!ok) {
      toast.error('Only .xlsx or .csv files are accepted.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('File is too large (max 5 MB).');
      return;
    }
    setFile(f);
  };

  const uploadAndPreview = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/events/bulk-import/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not parse spreadsheet.');
    }
  };

  const commitImport = async () => {
    if (!preview) return;
    const validRows = preview.rows.filter(r => r.valid).map(r => r.data);
    if (validRows.length === 0) {
      toast.error('No valid rows to import.');
      return;
    }
    setCommitting(true);
    try {
      const res = await api.post('/events/bulk-import/commit', { rows: validRows });
      setBatch(res.data);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not import. Please try again.');
    } finally {
      setCommitting(false);
    }
  };

  const startCheckout = async () => {
    if (!batch) return;
    try {
      const cfg = await api.get('/paypal/config');
      setPaypalClientId(cfg.data.client_id);
      const res = await api.post(`/events/bulk-import/${batch.batch_id}/checkout`);
      if (res.data.published) {
        toast.success('All drafts ready — no payment owed.');
        setStep(4);
        return;
      }
      setPaypalOrder({ order_id: res.data.order_id, amount_due: res.data.amount_due });
      setPaypalReady(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout.');
    }
  };

  const skipCheckoutForNow = () => setStep(4);

  // ---------- step renderers ----------

  const renderUploadStep = () => (
    <Card className="rounded-3xl border-slate-200">
      <CardContent className="p-6 sm:p-10 space-y-6">
        <div className="text-center">
          <FileSpreadsheet className="w-14 h-14 text-[#b456df] mx-auto mb-3" />
          <h2 className="text-2xl font-bold mb-2">Upload your spreadsheet</h2>
          <p className="text-slate-600 max-w-xl mx-auto">
            Save time by importing many events at once. Download our template, fill in your events,
            and drop the file below. We&apos;ll validate every row before anything gets created.
          </p>
        </div>

        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-[#b456df]/15 flex items-center justify-center flex-shrink-0">
                <Download className="w-5 h-5 text-[#b456df]" />
              </div>
              <div className="min-w-0">
                <p className="font-medium">Need the template?</p>
                <p className="text-sm text-slate-600 truncate">
                  .xlsx with headers + 3 sample rows. Edit and save, then come back here.
                </p>
              </div>
            </div>
            <Button
              onClick={downloadTemplate}
              data-testid="bulk-import-download-template"
              className="rounded-full bg-white text-[#b456df] border border-[#b456df] hover:bg-[#b456df]/5 flex-shrink-0"
            >
              Download
            </Button>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
            <p className="font-medium text-slate-700 mb-1">Columns inside the template (fill in Excel / Google Sheets):</p>
            <p className="leading-relaxed">
              <code className="bg-slate-100 px-1 py-0.5 rounded">title</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded">event_type</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded font-semibold text-[#b456df]">is_free</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded font-semibold text-[#b456df]">price</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded">description</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded">event_date</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded">start_time</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded">end_time</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded">venue_name</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded">address</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded">city</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded">state</code>{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded">zip_code</code>{' '}
              <span className="text-slate-400">+ a few more (skill levels, charity fields, etc.)</span>
            </p>
            <p className="mt-2 text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              <strong>Already downloaded an older template?</strong> Please download this one again — we just moved the <code className="bg-amber-100 px-1 rounded">price</code> column to be easier to find.
            </p>
          </div>
        </div>

        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${file ? 'border-[#1f8a6a] bg-[#56dfb4]/5' : 'border-slate-300 hover:border-[#b456df] hover:bg-[#b456df]/5'}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); }}
          onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
          role="button"
          tabIndex={0}
          data-testid="bulk-import-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_TYPES}
            className="hidden"
            data-testid="bulk-import-file-input"
            onChange={e => handleFile(e.target.files?.[0])}
          />
          {file ? (
            <>
              <FileCheck2 className="w-10 h-10 text-[#1f8a6a] mx-auto mb-3" />
              <p className="font-medium">{file.name}</p>
              <p className="text-sm text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB — click to choose a different file</p>
            </>
          ) : (
            <>
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="font-medium">Drag your .xlsx or .csv here</p>
              <p className="text-sm text-slate-500 mt-1">or click to browse</p>
            </>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            disabled={!file}
            onClick={uploadAndPreview}
            data-testid="bulk-import-preview-btn"
            className="rounded-full bg-[#b456df] hover:bg-[#9a3ec4] text-white px-8 gap-2"
          >
            Preview <ArrowRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Collapsible spreadsheet-rules reference. The same content is
            also baked into the template's "Instructions" sheet so
            instructors have it next to their data. */}
        <div className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="bulk-import-rules-panel">
          <button
            type="button"
            onClick={() => setShowRules(s => !s)}
            data-testid="bulk-import-rules-toggle"
            className="w-full flex items-center justify-between gap-3 px-5 py-4 bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <span className="flex items-center gap-2 font-medium text-slate-700">
              <BookOpen className="w-4 h-4" /> Spreadsheet rules &amp; allowed values
            </span>
            {showRules ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
          </button>
          {showRules && (
            <div className="px-5 py-4 text-sm text-slate-700 space-y-5">
              <section>
                <p className="font-semibold mb-1">File rules</p>
                <ul className="list-disc pl-5 space-y-0.5 text-slate-600">
                  <li>.xlsx or .csv (Google Sheets &rarr; <em>Download as .csv</em> works)</li>
                  <li>Max 5 MB, max 200 rows per upload</li>
                  <li>Column headers must match the template (case-insensitive)</li>
                </ul>
              </section>
              <section>
                <p className="font-semibold mb-1">Required columns (row rejected if blank)</p>
                <p className="text-slate-600 text-xs">
                  <code className="bg-slate-100 px-1 py-0.5 rounded">title</code>{' '}
                  <code className="bg-slate-100 px-1 py-0.5 rounded">event_type</code>{' '}
                  <code className="bg-slate-100 px-1 py-0.5 rounded">description</code>{' '}
                  <code className="bg-slate-100 px-1 py-0.5 rounded">event_date</code>{' '}
                  <code className="bg-slate-100 px-1 py-0.5 rounded">start_time</code>{' '}
                  <code className="bg-slate-100 px-1 py-0.5 rounded">end_time</code>{' '}
                  <code className="bg-slate-100 px-1 py-0.5 rounded">venue_name</code>{' '}
                  <code className="bg-slate-100 px-1 py-0.5 rounded">street_address</code>{' '}
                  <code className="bg-slate-100 px-1 py-0.5 rounded">city</code>{' '}
                  <code className="bg-slate-100 px-1 py-0.5 rounded">state</code>{' '}
                  <code className="bg-slate-100 px-1 py-0.5 rounded">zip_code</code>
                </p>
              </section>
              <section>
                <p className="font-semibold mb-1">event_type — pick one (matches the website)</p>
                <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-slate-600 list-disc pl-5">
                  <li><strong>Teaching Session</strong> — Instructional</li>
                  <li><strong>Tournament</strong> — Competitive</li>
                  <li><strong>Open Play</strong> — Group play</li>
                  <li><strong>Special Event</strong> — Themed / club</li>
                  <li><strong>Destination Event</strong> — Cruise, retreat, fly-in (shows 🌍 badge, bypasses distance filter)</li>
                  <li><strong>Charity Event</strong> — Posting fee waived; routes to admin review. Requires <code>charity_nonprofit_name</code> + <code>charity_website</code> + <code>charity_description</code>.</li>
                </ul>
              </section>
              <section>
                <p className="font-semibold mb-1">Format rules</p>
                <ul className="text-slate-600 list-disc pl-5 space-y-0.5">
                  <li><code>event_date</code>: <code>YYYY-MM-DD</code>. Cannot be in the past.</li>
                  <li><code>start_time</code> / <code>end_time</code>: <code>HH:MM</code> (24-hour). Start must be before end.</li>
                  <li><code>timezone</code>: ET, CT, MT, PT, AKT, HT. Defaults to ET.</li>
                  <li><code>state</code>: exactly 2 letters (e.g. NY).</li>
                  <li><code>zip_code</code>: 5 digits. Excel&apos;s <code>&quot;10601.0&quot;</code> or stripped leading zeros are auto-fixed.</li>
                  <li><code>location_type</code>: <code>public</code> or <code>private</code>.</li>
                  <li><code>max_attendees</code>: 1–500, or blank for unlimited.</li>
                  <li><code>skill_levels</code>: <code>;</code> or <code>,</code> separated. Canonical values: <code>all</code>, <code>beginner</code>, <code>intermediate</code>, <code>advanced</code>. Common synonyms like <em>&quot;Beginners&quot;</em>, <em>&quot;All Levels&quot;</em>, <em>&quot;Novice&quot;</em>, <em>&quot;Expert&quot;</em>, <em>&quot;Mixed&quot;</em> are auto-corrected.</li>
                  <li><code>is_free</code>: <code>yes</code> or <code>no</code>. For PAID events: set <code>no</code> AND fill in <code>price</code>.</li>
                  <li><code>price</code>: free text shown to attendees (e.g. <em>&quot;$50 includes lunch &amp; drinks&quot;</em>). Older spreadsheets using <code>cost_description</code> still work.</li>
                </ul>
              </section>
              <section>
                <p className="font-semibold mb-1">What&apos;s set automatically</p>
                <ul className="text-slate-600 list-disc pl-5 space-y-0.5">
                  <li>Host = you. Status = draft (nothing publishes until you publish or pay).</li>
                  <li>Posting fee computed from your subscription tier + <code>is_free</code>.</li>
                  <li>Banked free posts auto-apply to the cheapest rows in your batch.</li>
                  <li>Banner images, recurrence: set later from My Events.</li>
                </ul>
              </section>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderPreviewStep = () => (
    <Card className="rounded-3xl border-slate-200">
      <CardContent className="p-6 sm:p-8 space-y-5">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-2xl font-bold">Preview ({preview.total} rows)</h2>
          <div className="flex gap-2 text-sm">
            <span data-testid="bulk-import-valid-count" className="px-3 py-1 rounded-full bg-[#56dfb4]/15 text-[#1f8a6a] border border-[#56dfb4]/40">
              {preview.valid_count} valid
            </span>
            {preview.invalid_count > 0 && (
              <span data-testid="bulk-import-invalid-count" className="px-3 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                {preview.invalid_count} invalid
              </span>
            )}
            {preview.rows.some(r => (r.warnings || []).length > 0) && (
              <span data-testid="bulk-import-warning-count" className="px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                {preview.rows.filter(r => (r.warnings || []).length > 0).length} with warnings
              </span>
            )}
          </div>
        </div>

        {preview.invalid_count > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex gap-3 items-start">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p>
              Rows with errors won&apos;t be imported. Fix them in your spreadsheet and upload again,
              or continue with the {preview.valid_count} valid rows.
            </p>
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm" data-testid="bulk-import-preview-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Row</th>
                <th className="px-3 py-2 text-left">Title</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Venue</th>
                <th className="px-3 py-2 text-left">Pricing</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map(r => {
                // Surface what the instructor filled in for price so she can
                // sanity-check it before committing. `cost_description` is
                // the legacy field name; modern uploads write `price`. Both
                // come through, so check both for backwards-compat preview.
                const priceText = r.data.price || r.data.cost_description || '';
                const pricingLabel = r.data.is_free
                  ? <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#56dfb4]/15 text-[#1f8a6a] text-xs">Free</span>
                  : (priceText
                    ? <span className="text-slate-700 text-xs">{priceText}</span>
                    : <span className="text-amber-700 text-xs italic">no price filled in</span>);
                return (
                  <tr key={r.row_number} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-500">{r.row_number}</td>
                    <td className="px-3 py-2 font-medium truncate max-w-[260px]">{r.data.title || <em className="text-slate-400">(blank)</em>}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.data.event_date || '—'}</td>
                    <td className="px-3 py-2 text-slate-600 truncate max-w-[180px]">{r.data.venue_name || '—'}</td>
                    <td className="px-3 py-2 max-w-[160px]" data-testid={`bulk-import-row-price-${r.row_number}`}>
                      {pricingLabel}
                    </td>
                    <td className="px-3 py-2">
                      {r.valid ? (
                        <div className="space-y-1">
                          <span className="inline-flex items-center gap-1 text-[#1f8a6a] text-xs">
                            <CheckCircle2 className="w-3.5 h-3.5" /> OK
                          </span>
                          {(r.warnings || []).map((w, i) => (
                            <div key={i} className="flex items-start gap-1 text-amber-800 text-xs" data-testid={`bulk-import-row-warning-${r.row_number}`}>
                              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span>{w}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="inline-flex items-start gap-1 text-rose-700 text-xs">
                          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <span>{r.errors.join('; ')}</span>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center gap-3 pt-2">
          <Button
            onClick={() => { setStep(1); setPreview(null); setFile(null); }}
            data-testid="bulk-import-back-to-upload"
            className="rounded-full bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <Button
            disabled={committing || preview.valid_count === 0}
            onClick={commitImport}
            data-testid="bulk-import-commit-btn"
            className="rounded-full bg-[#b456df] hover:bg-[#9a3ec4] text-white px-8 gap-2"
          >
            {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>Import {preview.valid_count} as drafts <ArrowRight className="w-4 h-4" /></>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderConfirmStep = () => (
    <Card className="rounded-3xl border-slate-200">
      <CardContent className="p-6 sm:p-8 space-y-6">
        <div className="text-center">
          <CheckCircle2 className="w-14 h-14 text-[#1f8a6a] mx-auto mb-3" />
          <h2 className="text-2xl font-bold mb-2">Drafts created</h2>
          <p className="text-slate-600">
            <span className="font-medium">{batch.row_count}</span> draft{batch.row_count === 1 ? '' : 's'} saved to your account.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          {batch.free_posts_applied > 0 && (
            <div className="rounded-xl bg-[#56dfb4]/10 border border-[#56dfb4]/40 p-4">
              <p className="font-medium text-[#1f8a6a] flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> {batch.free_posts_applied} free post{batch.free_posts_applied === 1 ? '' : 's'} applied
              </p>
              <p className="text-xs text-[#1f8a6a]/80 mt-1">
                Banked from a coupon — applied to the cheapest event{batch.free_posts_applied === 1 ? '' : 's'} in your batch.
              </p>
            </div>
          )}
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
            <p className="text-slate-500 text-xs uppercase tracking-wide">Total due</p>
            <p className="text-2xl font-bold mt-1" data-testid="bulk-import-total-due">
              ${batch.total_due_usd.toFixed(2)}
            </p>
          </div>
        </div>

        {batch.total_due_usd > 0 && !paypalReady && (
          <div className="rounded-2xl border border-slate-200 p-5 space-y-3">
            <p className="text-sm text-slate-700">
              You can pay now (one combined PayPal charge for all events) or skip and pay each draft individually from My Events.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={startCheckout}
                data-testid="bulk-import-pay-now-btn"
                className="rounded-full bg-[#b456df] hover:bg-[#9a3ec4] text-white px-6"
              >
                Pay ${batch.total_due_usd.toFixed(2)} now
              </Button>
              <Button
                onClick={skipCheckoutForNow}
                data-testid="bulk-import-pay-later-btn"
                className="rounded-full bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 px-6"
              >
                I&apos;ll pay each from My Events
              </Button>
            </div>
          </div>
        )}

        {batch.total_due_usd <= 0 && (
          <div className="rounded-2xl bg-[#56dfb4]/10 border border-[#56dfb4]/40 p-5 text-center">
            <p className="text-[#1f8a6a] font-medium">No payment needed — all events covered by your banked free posts.</p>
            <Button
              onClick={() => setStep(4)}
              data-testid="bulk-import-finish-free-btn"
              className="rounded-full bg-[#1f8a6a] hover:bg-[#16725a] text-white px-6 mt-3"
            >
              Finish
            </Button>
          </div>
        )}

        {paypalReady && paypalOrder && paypalClientId && (
          <div className="rounded-2xl border border-slate-200 p-5">
            <p className="text-sm text-slate-600 mb-3">
              Paying ${batch.total_due_usd.toFixed(2)} for {batch.row_count} event{batch.row_count === 1 ? '' : 's'}.
            </p>
            <PayPalScriptProvider options={{ 'client-id': paypalClientId, currency: 'USD' }}>
              <PayPalButtons
                createOrder={() => paypalOrder.order_id}
                onApprove={async () => {
                  try {
                    await api.post(`/events/bulk-import/${batch.batch_id}/capture/${paypalOrder.order_id}`);
                    toast.success('Payment captured — all events published.');
                    setStep(4);
                  } catch (e) {
                    toast.error('Capture failed. Please contact support.');
                  }
                }}
                onError={() => toast.error('PayPal error. Please try again.')}
                style={{ layout: 'horizontal', tagline: false }}
              />
            </PayPalScriptProvider>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderDoneStep = () => (
    <Card className="rounded-3xl border-slate-200">
      <CardContent className="p-10 text-center space-y-5">
        <CheckCircle2 className="w-16 h-16 text-[#1f8a6a] mx-auto" />
        <h2 className="text-3xl font-bold">All set!</h2>
        <p className="text-slate-600 max-w-md mx-auto">
          {batch.row_count} draft event{batch.row_count === 1 ? '' : 's'} saved. Head to My Events to review each one and publish when ready.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/events/my-events">
            <Button data-testid="bulk-import-go-to-my-events" className="rounded-full bg-[#b456df] hover:bg-[#9a3ec4] text-white px-8">
              Go to My Events
            </Button>
          </Link>
          <Button
            onClick={() => { setStep(1); setFile(null); setPreview(null); setBatch(null); setPaypalReady(false); setPaypalOrder(null); }}
            data-testid="bulk-import-upload-another"
            className="rounded-full bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 px-8"
          >
            Upload another batch
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="bulk-import-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Bulk Upload Events</h1>
        <p className="text-slate-500 mt-1">Import many events at once from a spreadsheet.</p>
      </div>
      <StepBar step={step} />
      {step === 1 && renderUploadStep()}
      {step === 2 && preview && renderPreviewStep()}
      {step === 3 && batch && renderConfirmStep()}
      {step === 4 && batch && renderDoneStep()}
    </div>
  );
}
